import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { authAPI } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, ArrowLeft, Sun, Moon, CheckCircle } from 'lucide-react';
import { MetaTradifyLogo } from '@/components/MetaTradifyLogo';
import { toast } from 'sonner';

export default function ForgotPasswordPage() {
  const { theme, toggleTheme } = useTheme();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }


    setLoading(true);
    try {
      await authAPI.forgotPassword({ email });
      setSent(true);
      toast.success('If the email exists, a reset link has been sent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }

  };

  return (<div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center px-4" data-testid="forgot-password-page">


    {/* Theme Toggle */}
    <button
      onClick={toggleTheme}
      data-testid="forgot-theme-toggle"
      className="absolute top-4 right-4 p-2 rounded-lg bg-[var(--hover-bg)] border border-[var(--border-subtle)] transition-all z-10"
    >
      {theme === 'dark'
        ? <Sun className="w-4 h-4 text-amber-400" />
        : <Moon className="w-4 h-4 text-green-500" />
      }
    </button>

    {/* Background Glow */}
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute top-1/3 right-1/4 w-80 h-80 bg-green-500/5 rounded-full blur-[100px]" />
    </div>

    <div className="w-full max-w-[400px] relative">

      {/* Header */}
      <div className="text-center mb-8">
        <Link to="/" className="inline-flex items-center gap-2 mb-6">
          <MetaTradifyLogo size={32} />
          <span className="text-lg font-bold font-['Outfit'] text-[var(--text-primary)]">
            metaTradify
          </span>
        </Link>

        <h1 className="text-2xl font-bold font-['Outfit'] tracking-tight text-[var(--text-primary)]">
          Reset Password
        </h1>

        <p className="text-sm text-[var(--text-secondary)] mt-1">
          Enter your email to receive a reset link
        </p>
      </div>

      {!sent ? (
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">

          {/* Email Input */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm text-[var(--text-secondary)]">
              Email Address
            </Label>

            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-tertiary)]" />

              <Input
                id="email"
                type="email"
                data-testid="forgot-email-input"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-[var(--bg-surface)] border-[var(--border-subtle)] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:border-green-500 focus:ring-green-500/20 h-11 pl-10"
              />
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            data-testid="forgot-submit-btn"
            className="w-full h-11 bg-green-500 hover:bg-green-600 text-white rounded text-sm font-medium transition-all duration-200 disabled:opacity-50"
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>

      ) : (

        /* Success State */
        <div className="text-center space-y-4" data-testid="forgot-success">

          <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/15 flex items-center justify-center">
            <CheckCircle className="w-7 h-7 text-emerald-400" />
          </div>

          <p className="text-sm text-[var(--text-secondary)]">
            If an account with this email exists, a password reset link has been sent.
          </p>

          <p className="text-xs text-[var(--text-tertiary)]">
            Please check your inbox and spam folder.
          </p>

        </div>
      )}

      {/* Back to Login */}
      <div className="mt-6 text-center">
        <Link
          to="/login-dashboard"
          className="inline-flex items-center gap-1.5 text-sm text-[var(--text-secondary)] hover:text-green-400 transition-colors"
          data-testid="back-to-login-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Login
        </Link>
      </div>

    </div>
  </div>
  );
}
